import { createApp, type Component } from 'vue';
import type { RouteRecordRaw } from 'vue-router';
import { createPinia } from 'pinia';
import { VueQueryPlugin } from '@tanstack/vue-query';
import I18NextVue from 'i18next-vue';
import { Toaster, toast } from 'vue-sonner';

import { usePlatformConfigStore } from '@/stores/platformConfig';
import { queryClient } from '@/queries/queryClient';
import api from '@/helpers/api';
import i18next, { i18nUsage } from '@/helpers/i18n';
import { I18N_USAGE_FLAG } from '@cutr/constants/i18n-usage';
import Tracker from '@/helpers/tracker';
import FormatPlugin from '@/helpers/format-plugin';
import PlatformConfigPlugin from '@/helpers/platform-config-plugin';
import HelpLinksPlugin from '@/helpers/help-links-plugin';

import { initFormKit } from './formkit';
import { initSentry } from './sentry';
import { installUnleash } from './unleash';
import { installFloatingVue } from './floatingVue';
import { registerGlobalComponents } from './globalComponents';
import { createCutrRouter } from './router';
import { startSessionSync } from './session';
import baseURL from '@/helpers/base-url';
import { isFeatureEnabled } from '@/helpers/unleash';

type CutrAppOptions = {
  rootComponent: Component;
  routes: RouteRecordRaw[];
};

export function createCutrApp({ rootComponent, routes }: CutrAppOptions) {
  const app = createApp(rootComponent);

  initFormKit(app);
  const unleashClient = installUnleash(app);
  i18nUsage.start({
    url: `${baseURL}/v2/i18n-usage`,
    isEnabled: () => isFeatureEnabled(I18N_USAGE_FLAG),
  });
  installFloatingVue(app);
  app.use(I18NextVue, { i18next });
  app.use(FormatPlugin);
  app.use(PlatformConfigPlugin);
  app.use(HelpLinksPlugin);

  app.config.globalProperties.$api = api;
  app.component('Toaster', Toaster);
  app.config.globalProperties.$toast = toast;

  registerGlobalComponents(app);

  app.use(createPinia());
  app.use(VueQueryPlugin, { queryClient });
  // Start polling the platform config at boot rather than on its first read.
  usePlatformConfigStore();

  const router = createCutrRouter(routes);
  app.use(Tracker, { router });
  app.use(router);
  initSentry(app, router);

  return {
    app,
    router,
    mount(selector: string) {
      app.mount(selector);
      startSessionSync(unleashClient);
    },
  };
}
